import React, { useState } from 'react'
import { ChevronDown, ChevronRight, Pencil, Trash2, Plus, Move, Search } from 'lucide-react'
import type { ProductCategory } from '@/types/pos'
import { buildCategoryTree, filterCategoryTree, getDescendantIds, type CategoryTreeNode } from '@/lib/categoryTree'

interface CategoryTreeManageProps {
  categories: ProductCategory[]
  onEdit: (id: string, updates: { name?: string }) => Promise<void>
  onDelete: (id: string) => void
  onAddSub: (parentId: string | null) => void
  onMove: (id: string, newParentId: string | null) => Promise<void>
  onReorder: (id: string, direction: 'up' | 'down') => Promise<void>
}

function TreeNode({
  node,
  depth,
  categories,
  onEdit,
  onDelete,
  onAddSub,
  onMove,
  onReorder,
  siblingIndex,
  siblingCount,
}: {
  node: CategoryTreeNode
  depth: number
  categories: ProductCategory[]
  onEdit: (id: string, updates: { name?: string }) => Promise<void>
  onDelete: (id: string) => void
  onAddSub: (parentId: string | null) => void
  onMove: (id: string, newParentId: string | null) => Promise<void>
  onReorder: (id: string, direction: 'up' | 'down') => Promise<void>
  siblingIndex: number
  siblingCount: number
}) {
  const [expanded, setExpanded] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(node.category.name)
  const [showMove, setShowMove] = useState(false)
  const id = node.category.id || (node.category as any)._id
  const hasChildren = node.children.length > 0

  const handleSaveEdit = async () => {
    const trimmed = editName.trim()
    if (trimmed && trimmed !== node.category.name) {
      await onEdit(id, { name: trimmed })
    }
    setEditing(false)
  }

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center gap-1 py-2 px-2 rounded-lg hover:bg-muted/50 group border border-transparent hover:border-border/50"
        style={{ paddingLeft: 8 + depth * 20 }}
      >
        <button
          type="button"
          className="p-0.5 rounded hover:bg-muted text-muted-foreground"
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {hasChildren ? (
            expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
          ) : (
            <span className="w-4 inline-block" />
          )}
        </button>
        {editing ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSaveEdit}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') { setEditName(node.category.name); setEditing(false) } }}
            className="flex-1 min-w-0 px-2 py-1 text-sm bg-background border border-primary rounded"
            autoFocus
          />
        ) : (
          <span className="flex-1 min-w-0 text-sm font-medium truncate">{node.category.name}</span>
        )}
        {!editing && (
          <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground" title="Edit" onClick={() => { setEditing(true); setEditName(node.category.name) }}>
              <Pencil className="w-3 h-3" />
            </button>
            <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground" title="Add sub-genre" onClick={() => onAddSub(id)}>
              <Plus className="w-3 h-3" />
            </button>
            <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground" title="Move" onClick={() => setShowMove((m) => !m)}>
              <Move className="w-3 h-3" />
            </button>
            {siblingCount > 0 && (
              <>
                <button type="button" className="p-0.5 rounded hover:bg-muted text-muted-foreground" title="Move up" disabled={siblingIndex === 0} onClick={() => onReorder(id, 'up')}>
                  <span className="text-[10px] font-bold leading-none">↑</span>
                </button>
                <button type="button" className="p-0.5 rounded hover:bg-muted text-muted-foreground" title="Move down" disabled={siblingIndex === siblingCount - 1} onClick={() => onReorder(id, 'down')}>
                  <span className="text-[10px] font-bold leading-none">↓</span>
                </button>
              </>
            )}
            <button type="button" className="p-1 rounded hover:bg-red-500/10 text-red-400" title="Delete" onClick={() => onDelete(id)}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
      {showMove && (
        <MoveParentDropdown
          categories={categories}
          currentId={id}
          onSelect={(parentId) => { onMove(id, parentId); setShowMove(false) }}
          onClose={() => setShowMove(false)}
        />
      )}
      {hasChildren && expanded && (
        <div className="flex flex-col">
          {node.children.map((child, idx) => (
            <TreeNode
              key={child.category.id || (child.category as any)._id}
              node={child}
              depth={depth + 1}
              categories={categories}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddSub={onAddSub}
              onMove={onMove}
              onReorder={onReorder}
              siblingIndex={idx}
              siblingCount={node.children.length}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function MoveParentDropdown({
  categories,
  currentId,
  onSelect,
  onClose,
}: {
  categories: ProductCategory[]
  currentId: string
  onSelect: (parentId: string | null) => void
  onClose: () => void
}) {
  const tree = buildCategoryTree(categories)
  const descendantIds = new Set(getDescendantIds(categories, currentId))
  descendantIds.add(currentId)

  function renderOptions(nodes: CategoryTreeNode[], depth: number) {
    return nodes
      .filter((n) => !descendantIds.has(n.category.id || (n.category as any)._id))
      .map((node) => {
        const id = node.category.id || (node.category as any)._id
        return (
          <React.Fragment key={id}>
            <button
              type="button"
              className="w-full text-left py-1.5 px-3 hover:bg-muted text-sm rounded-md"
              style={{ paddingLeft: 12 + depth * 16 }}
              onClick={() => onSelect(id)}
            >
              {node.category.name}
            </button>
            {node.children.length > 0 && renderOptions(node.children, depth + 1)}
          </React.Fragment>
        )
      })
  }

  return (
    <div className="ml-8 mt-1 p-2 rounded-lg border border-border bg-muted/30 max-h-48 overflow-y-auto">
      <p className="text-xs text-muted-foreground mb-2">Move to:</p>
      <button type="button" className="w-full text-left py-1.5 px-3 hover:bg-muted text-sm rounded-md font-medium" onClick={() => onSelect(null)}>
        Root (no parent)
      </button>
      {renderOptions(tree, 0)}
      <button type="button" className="mt-2 text-xs text-muted-foreground hover:underline" onClick={onClose}>
        Cancel
      </button>
    </div>
  )
}

export const CategoryTreeManage: React.FC<CategoryTreeManageProps> = ({
  categories,
  onEdit,
  onDelete,
  onAddSub,
  onMove,
  onReorder,
}) => {
  const [search, setSearch] = useState('')
  const fullTree = buildCategoryTree(categories)
  const tree = filterCategoryTree(fullTree, search)
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search genres..."
          className="w-full pl-8 pr-3 py-2 text-sm bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
        />
      </div>
      <div className="space-y-0.5">
        {fullTree.length === 0 && <p className="text-sm text-muted-foreground py-2">No genres yet. Add one above.</p>}
        {fullTree.length > 0 && tree.length === 0 && (
          <p className="text-sm text-muted-foreground py-2">No genres match &quot;{search}&quot;</p>
        )}
        {tree.map((node, idx) => (
          <TreeNode
            key={node.category.id || (node.category as any)._id}
            node={node}
            depth={0}
            categories={categories}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddSub={onAddSub}
            onMove={onMove}
            onReorder={onReorder}
            siblingIndex={idx}
            siblingCount={tree.length}
          />
        ))}
      </div>
    </div>
  )
}
